import axios from 'axios'

const API_URL = process.env.VUE_APP_AI_AGENT_API_BASE_URL

class KnowledgeBaseService {
  // ==========================================
  // Knowledge Base CRUD Operations
  // ==========================================

  /**
   * Get all knowledge bases for a specific agent
   * @param {number} agentId - The agent ID
   * @returns {Promise} API response with knowledge bases list
   */
  getKnowledgeBases (agentId) {
    return axios.get(API_URL + 'agents/' + agentId + '/KnowledgeBases')
  }

  /**
   * Get all knowledge bases across all agents in the current scope.
   * Used by the global KB list page.
   */
  getAllKnowledgeBases () {
    return axios.get(API_URL + 'knowledge-bases')
  }

  /**
   * Get knowledge bases for a specific project
   * @param {number} agentId - The agent ID
   * @param {number} projectId - The project ID
   * @returns {Promise} API response with knowledge bases list
   */
  getKnowledgeBasesByProject (agentId, projectId) {
    return axios.get(API_URL + 'agents/' + agentId + '/projects/' + projectId + '/KnowledgeBases')
  }

  /**
   * Get a specific knowledge base by ID
   * @param {number} agentId - The agent ID
   * @param {number} knowledgeBaseId - The knowledge base ID
   * @returns {Promise} API response with knowledge base details
   */
  getKnowledgeBaseById (agentId, knowledgeBaseId) {
    return axios.get(API_URL + 'agents/' + agentId + '/KnowledgeBases/' + knowledgeBaseId)
  }

  /**
   * Create a new knowledge base for an agent or project
   * @param {number} agentId - The agent ID
   * @param {Object} knowledgeBaseData - Knowledge base data (includes optional projectId)
   * @returns {Promise} API response with created knowledge base
   */
  createKnowledgeBase (agentId, knowledgeBaseData) {
    return axios.post(API_URL + 'agents/' + agentId + '/KnowledgeBases', knowledgeBaseData)
  }

  /**
   * Create a knowledge base for a specific project (convenience method)
   * @param {number} agentId - The agent ID
   * @param {number} projectId - The project ID
   * @param {Object} knowledgeBaseData - Knowledge base data
   * @returns {Promise} API response with created knowledge base
   */
  createProjectKnowledgeBase (agentId, projectId, knowledgeBaseData) {
    // Add projectId to the data
    const dataWithProject = Object.assign({}, knowledgeBaseData, { projectId: projectId })
    return axios.post(API_URL + 'agents/' + agentId + '/KnowledgeBases', dataWithProject)
  }

  /**
   * Update an existing knowledge base
   * @param {number} agentId - The agent ID
   * @param {number} knowledgeBaseId - The knowledge base ID
   * @param {Object} knowledgeBaseData - Updated knowledge base data
   * @returns {Promise} API response with updated knowledge base
   */
  updateKnowledgeBase (agentId, knowledgeBaseId, knowledgeBaseData) {
    return axios.put(API_URL + 'agents/' + agentId + '/KnowledgeBases/' + knowledgeBaseId, knowledgeBaseData)
  }

  /**
   * Delete a knowledge base
   * @param {number} agentId - The agent ID
   * @param {number} knowledgeBaseId - The knowledge base ID
   * @returns {Promise} API response
   */
  deleteKnowledgeBase (agentId, knowledgeBaseId) {
    return axios.delete(API_URL + 'agents/' + agentId + '/KnowledgeBases/' + knowledgeBaseId)
  }

  /**
   * Toggle knowledge base active status
   * @param {number} agentId - The agent ID
   * @param {number} knowledgeBaseId - The knowledge base ID
   * @returns {Promise} API response
   */
  toggleKnowledgeBase (agentId, knowledgeBaseId) {
    return axios.patch(API_URL + 'agents/' + agentId + '/KnowledgeBases/' + knowledgeBaseId + '/toggle')
  }

  /**
   * Get knowledge base statistics
   * @param {number} agentId - The agent ID
   * @param {number} knowledgeBaseId - The knowledge base ID
   * @returns {Promise} API response with statistics
   */
  getKnowledgeBaseStatistics (agentId, knowledgeBaseId) {
    return axios.get(API_URL + 'agents/' + agentId + '/KnowledgeBases/' + knowledgeBaseId + '/statistics')
  }

  // ==========================================
  // Search Operations
  // ==========================================

  /**
   * Search within a knowledge base
   * @param {number} knowledgeBaseId - The knowledge base ID
   * @param {Object} searchData - Search parameters (query, maxResults, etc.)
   * @returns {Promise} API response with search results
   */
  searchKnowledgeBase (knowledgeBaseId, searchData) {
    return axios.post(API_URL + 'knowledge-bases/' + knowledgeBaseId + '/search', searchData)
  }

  /**
   * Search for context within a knowledge base
   * @param {number} knowledgeBaseId - The knowledge base ID
   * @param {Object} contextData - Context search parameters
   * @returns {Promise} API response with context
   */
  searchContext (knowledgeBaseId, contextData) {
    return axios.post(API_URL + 'knowledge-bases/' + knowledgeBaseId + '/search/context', contextData)
  }

  /**
   * Get relevant chunks from knowledge base
   * @param {number} knowledgeBaseId - The knowledge base ID
   * @param {Object} params - Query parameters
   * @returns {Promise} API response with relevant chunks
   */
  getRelevantChunks (knowledgeBaseId, params) {
    params = params || {}
    return axios.get(API_URL + 'knowledge-bases/' + knowledgeBaseId + '/search/relevant-chunks', { params: params })
  }

  // ==========================================
  // Helper Methods
  // ==========================================

  /**
   * Get owner type badge info
   * @param {string} ownerType - 'Agent' or 'Project'
   * @returns {Object} Badge configuration { text, class }
   */
  getOwnerTypeBadge (ownerType) {
    if (ownerType === 'Project') {
      return {
        text: 'Project KB',
        class: 'badge-primary'
      }
    }
    return {
      text: 'Agent KB',
      class: 'badge-info'
    }
  }

  /**
   * Format KB stats for display
   * @param {Object} kb - Knowledge base object
   * @returns {string} Formatted stats string
   */
  formatStats (kb) {
    const docs = kb.totalDocuments || 0
    const chunks = kb.totalChunks || 0
    return docs + ' docs, ' + chunks + ' chunks'
  }
}

export default new KnowledgeBaseService()
